import React, { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Info,
  MapPin,
  Search,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const STATUS_STYLES = {
  pending: { text: 'Pending approval', className: 'badge bg-gold-100 text-gold-800' },
  approved: { text: 'Approved — ready for collection', className: 'badge-success' },
  issued: { text: 'Issued', className: 'badge-navy' },
  returned: { text: 'Returned', className: 'badge-neutral' },
  default: { text: 'Not requested', className: 'badge-neutral' },
};

const statusBadge = (status) => STATUS_STYLES[status] || STATUS_STYLES.default;

const DetailRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 border-b border-ink-100 py-2 last:border-0">
    <dt className="flex-shrink-0 text-sm text-ink-500">{label}</dt>
    <dd
      className={`min-w-0 break-words text-right text-sm font-medium text-ink-900 ${
        mono ? 'font-mono' : ''
      }`}
    >
      {value || '—'}
    </dd>
  </div>
);

/**
 * Public gown-request flow: verify a graduate, then submit a gown request.
 * Rendered by src/pages/GownRequestPage.jsx.
 */
const GownRequestForm = () => {
  const [adminNumber, setAdminNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [graduate, setGraduate] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);

  const handleSearch = async () => {
    if (!adminNumber.trim()) {
      toast.error('Please enter your admission number');
      return;
    }

    setLoading(true);
    setGraduate(null);
    setRequestStatus(null);

    try {
      const response = await api.get(`/graduates/search?query=${encodeURIComponent(adminNumber)}`);

      if (response.data) {
        let activeGraduationDay = null;
        try {
          const activeDayRes = await api.get('/graduation-days/active');
          activeGraduationDay = activeDayRes.data;
        } catch (err) {
          console.error('Error fetching active graduation day:', err);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let ceremonyDate = null;
        if (response.data.ceremony_date) {
          ceremonyDate = new Date(response.data.ceremony_date);
          ceremonyDate.setHours(0, 0, 0, 0);
        } else if (activeGraduationDay && activeGraduationDay.ceremony_date) {
          ceremonyDate = new Date(activeGraduationDay.ceremony_date);
          ceremonyDate.setHours(0, 0, 0, 0);
        }

        const isGraduationPast = ceremonyDate && ceremonyDate < today;

        setGraduate({ ...response.data, isGraduationPast, ceremonyDate });

        try {
          const gownCheck = await api.get(`/gowns/check-request/${response.data.id}`);
          if (gownCheck.data.hasRequest) {
            setRequestStatus(gownCheck.data.status);
          }
        } catch (gownError) {
          console.error('Error checking gown request:', gownError);
        }
      } else {
        toast.error('No graduate found with this admission');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('No graduate found with this admission number');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestGown = async () => {
    if (!graduate) return;

    setRequesting(true);
    try {
      const response = await api.post('/gowns/request', {
        graduate_id: graduate.id,
        gown_size: null,
      });

      if (response.data.success) {
        setRequestStatus('pending');
        toast.success(
          response.data.message ||
            'Gown request submitted successfully! Please wait for admin approval.'
        );
      } else {
        toast.error(response.data.message || 'Failed to submit gown request');
      }
    } catch (error) {
      console.error('Request error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit gown request');
    } finally {
      setRequesting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="container-page py-6 sm:py-8">
      <div className="mx-auto max-w-2xl">
        <div className="card">
          <label htmlFor="gown-admin-number" className="label">
            Admission number
          </label>
          <div className="relative">
            <input
              id="gown-admin-number"
              type="text"
              placeholder="e.g. DEE/001/S2021"
              value={adminNumber}
              onChange={(e) => setAdminNumber(e.target.value)}
              className="input-field h-14 pr-32 text-base"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={loading}
              className="btn-primary absolute inset-y-1.5 right-1.5 min-h-0 px-4 text-sm"
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Verifying
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" aria-hidden="true" />
                  Verify
                </>
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-ink-500">
            Enter the admission number printed on your graduation card.
          </p>

          {graduate && (
            <div className="mt-6 border-t border-ink-200 pt-5">
              <h3 className="mb-3 text-base font-bold text-ink-900">Graduate details</h3>

              <dl className="rounded-xl bg-ink-50 px-4 py-2">
                <DetailRow label="Name" value={graduate.name} />
                <DetailRow label="Admin number" value={graduate.admin_number} mono />
                <DetailRow
                  label="Course"
                  value={`${graduate.course_name || ''}${
                    graduate.level_number
                      ? ` (Level ${graduate.level_number})`
                      : graduate.level_name
                      ? ` (${graduate.level_name})`
                      : ''
                  }`}
                />
                <DetailRow label="Department" value={graduate.department_name} />
                <DetailRow label="Graduation year" value={graduate.graduation_year} />
                {graduate.ceremonyDate && (
                  <DetailRow label="Ceremony date" value={formatDate(graduate.ceremonyDate)} />
                )}
              </dl>

              {graduate.isGraduationPast && (
                <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3">
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Graduation has passed</p>
                    <p className="mt-1 text-xs text-red-600">
                      Gown requests are only available for upcoming graduations. Please
                      contact the registrar&rsquo;s office for assistance.
                    </p>
                  </div>
                </div>
              )}

              {requestStatus ? (
                <div className="mt-4 rounded-xl border border-ink-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ink-700">Request status</span>
                    <span className={statusBadge(requestStatus).className}>
                      {statusBadge(requestStatus).text}
                    </span>
                  </div>

                  {requestStatus === 'approved' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      Your gown is ready for collection. Please visit the registrar&rsquo;s
                      office.
                    </p>
                  )}
                  {requestStatus === 'issued' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-brand-700">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      Your gown has been issued. Please return it after the ceremony.
                    </p>
                  )}
                  {requestStatus === 'returned' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-500">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      Gown has been returned. Thank you!
                    </p>
                  )}
                  {requestStatus === 'pending' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-gold-700">
                      <Clock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      Your request is pending approval. You will be notified once approved.
                    </p>
                  )}
                </div>
              ) : (
                !graduate.isGraduationPast && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleRequestGown}
                      disabled={requesting}
                      className="btn-secondary w-full"
                    >
                      {requesting ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-950/30 border-t-brand-950" />
                          Submitting
                        </>
                      ) : (
                        'Request gown'
                      )}
                    </button>
                    <p className="mt-2 text-center text-xs text-ink-500">
                      Requests are processed by the registrar. You will receive updates via
                      SMS.
                    </p>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <p className="mt-6 inline-flex items-center gap-1.5 text-xs text-ink-500">
          <Info className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          For inquiries, contact the registrar&rsquo;s office or call +254 719 508 039
        </p>
      </div>
    </div>
  );
};

export default GownRequestForm;
